import {
  FilterController,
  type FilterControllerSnapshot,
  type FilterGroup,
  type FilterSelection,
  type SearchToken,
} from "@carbon6/c6-frontend-design";
import { getDatasetFacets, type DatasetFacetGates, type DatasetFacets } from "@/api/datasetFacets";
import type { Lifecycle } from "@/lifecycle";
import { createDebouncedAction, type DebouncedAction } from "@/utils";
import { applyDatasetFacetCounts, stripFacetCounts } from "./datasetFacetCounts";
import { splitLabelToken, type LabelConditionOption } from "./datasetFilters";
import {
  addFilterBookmark,
  defaultFilterBookmarks,
  loadFilterBookmarks,
  removeFilterBookmark,
  saveFilterBookmarks,
  type DatasetFilterSurface,
  type FilterBookmark,
} from "./datasetFilterBookmarks";
import { loadFilterMemory, saveFilterMemory, type StoredFilterValues } from "./datasetFilterMemory";
import { DatasetSearchController, type DatasetSearchSnapshot } from "./DatasetSearchController";
import { loadDatasetLabelFilterCatalog, type DatasetMatchMode } from "./labelFilterQuery";

// facet/토큰 선택 변경을 묶는 debounce(ms)다. 연속 선택을 한 요청으로 만든다. 검색은 SearchController 가
// 자체 debounce 를 하므로 여기서 다시 묶지 않는다.
const FILTER_COMMIT_DEBOUNCE_MS = 300;

export type DatasetFilterDefaultValues = {
  searchQuery?: string;
  selection?: FilterSelection;
  labelTokens?: readonly SearchToken[];
  matchMode?: DatasetMatchMode;
};

export type DatasetFilterControllerOptions = {
  // 이 서비스 화면이 정한 초기화 기준이다. 사용자 기억값과 달리 제품 기본 계약이고, reset() 이 복원한다.
  defaultValues?: DatasetFilterDefaultValues;
  // criteria 변경을 호출자(테이블 소스)에 전달하는 콜백이다. 각 소스의 set* 메서드에 연결한다.
  onSearchQueryChange: (query: string) => void;
  // facet 선택과 라벨 토큰을 병합한 최종 selection 과 일치 방식(any/all)이다. createLabelFilterQuery 가
  // 이 값을 facet 파라미터·labels_all/any·filter_match 로 변환한다.
  onFilterSelectionChange: (selection: FilterSelection, mode: DatasetMatchMode) => void;
  resolveErrorMessage: (error: unknown) => string;
};

export type DatasetFilterSnapshot = {
  search: DatasetSearchSnapshot;
  filter: FilterControllerSnapshot;
  // 라벨 조건(generic 라벨)이다. 적용된 조건, 시트 입력값, 후보 카탈로그를 담는다. 후보는 검색
  // 콤보박스 드롭다운과 시트의 토큰 입력이 함께 쓴다.
  labelTokens: readonly SearchToken[];
  labelTokenInput: string;
  labelTokenOptions: readonly LabelConditionOption[];
  // 카탈로그 라벨 색(`key:value` → 0xRRGGBB)이다. 추천 후보 행의 칩은 facet 값에도 라벨 색을 적용한다.
  labelColors: ReadonlyMap<string, number>;
  // facet·라벨 조건 사이의 일치 방식이다(all=AND 기본 / any=OR).
  matchMode: DatasetMatchMode;
  // 필터 레일에 항상 표시할 북마크(순서 = 레일 표시 순서)와 고급 필터 시트 열림 상태다. 레일과
  // 진입 버튼(다른 표시 계층)이 같은 상태를 보도록 컨트롤러가 소유한다.
  bookmarks: readonly FilterBookmark[];
  advancedOpen: boolean;
  // 필터 조건 기억(opt-in)이다. 켜면 facet 선택·라벨 조건·조합 방식을 uid·표면 스코프로 저장하고 복원한다.
  // 북마크(노출 목록)는 이 스위치와 관계없이 항상 저장한다. 검색어는 저장하지 않는다.
  rememberFilters: boolean;
  // 드롭다운 선택지에 facets 개수가 있는지 나타낸다. 시트는 이 값으로 모집단 캡션의 표시 여부를 정한다.
  facetCountsShown: boolean;
  loading: boolean;
  errorMessage: string | null;
};

/**
 * Dataset / 북마크 / Review 가 함께 쓰는 단일 필터 컨트롤러다.
 *
 * 다음을 한 곳에서 소유한다:
 * - 순수 텍스트 검색(DatasetSearchController)
 * - facet(FilterController)
 * - 라벨 토큰 검색(generic 라벨)
 * - 라벨 카탈로그 로딩
 * 변경은 생성자 콜백 2종으로 호출자(각 페이지의 테이블 소스)에 전달한다. facet 선택과 라벨 토큰은
 * 하나의 selection 으로 병합해 알린다. 테이블 소스는 createLabelFilterQuery 로 그 값을 변환한다.
 * 뷰(`DatasetFilter`)는 `subscribe`/`getSnapshot` 으로 구독한다.
 */
export class DatasetFilterController implements Lifecycle {
  readonly filterController: FilterController;

  private readonly searchController: DatasetSearchController;
  private readonly filterCommitDebounce: DebouncedAction;
  private readonly options: DatasetFilterControllerOptions;
  private readonly defaultLabelTokens: readonly SearchToken[];
  private readonly defaultMatchMode: DatasetMatchMode;
  private readonly listeners = new Set<() => void>();
  private readonly unsubscribers: Array<() => void> = [];

  private facetSelection: FilterSelection = {};
  // 라벨·하드웨어 카탈로그로 만든 선택지다(개수 없음). facets 를 받을 때마다 이 선택지에 개수를 추가한다.
  private catalogGroups: readonly FilterGroup[] = [];
  private facetCounts: DatasetFacets | null = null;
  // facets 요청 세대다. 폴더를 빠르게 바꾸면 늦게 온 이전 폴더 응답을 버린다.
  private facetRequest = 0;
  private labelTokens: readonly SearchToken[];
  private labelTokenInput = "";
  private labelTokenOptions: readonly LabelConditionOption[] = [];
  private labelColors: ReadonlyMap<string, number> = new Map();
  private matchMode: DatasetMatchMode;
  private bookmarks: readonly FilterBookmark[] = defaultFilterBookmarks();
  private bookmarkUid: string | null = null;
  private bookmarkSurface: DatasetFilterSurface = "datasetLibrary";
  private bookmarkScopeLoaded = false;
  private advancedOpen = false;
  private rememberFilters = false;
  // 스코프를 로드할 때 복원을 기다리는 값이다. 카탈로그(그룹)를 로드한 뒤에 적용해야 normalize 가 버리지 않는다.
  private pendingRestore: StoredFilterValues | null = null;
  private loading = false;
  private errorMessage: string | null = null;
  private snapshot: DatasetFilterSnapshot;
  private disposed = true;

  constructor(options: DatasetFilterControllerOptions) {
    this.options = options;
    const defaultValues = options.defaultValues ?? {};
    this.defaultLabelTokens = copySearchTokens(defaultValues.labelTokens ?? []);
    this.defaultMatchMode = defaultValues.matchMode ?? "all";
    this.labelTokens = copySearchTokens(this.defaultLabelTokens);
    this.matchMode = this.defaultMatchMode;
    this.searchController = new DatasetSearchController({
      initialQuery: defaultValues.searchQuery,
      onQueryChange: (query) => options.onSearchQueryChange(query),
    });
    this.filterCommitDebounce = createDebouncedAction(FILTER_COMMIT_DEBOUNCE_MS);
    // facet 커밋 콜백은 생성자가 아니라 activateInnerControllers 의 subscribeSelectionChange 로
    // 등록한다. c6 FilterController.dispose() 가 selection 리스너를 비운다. 그래서 재구독 경로에
    // 두어야 StrictMode 재사용 뒤에도 다시 동작한다.
    this.filterController = new FilterController({
      groups: [],
      initialSelection: defaultValues.selection,
    });
    this.snapshot = this.computeSnapshot();
  }

  getSnapshot = (): DatasetFilterSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** 검색·facet 내부 구독을 활성화한다. StrictMode 재마운트에서는 dispose 뒤에 다시 호출할 수 있다. */
  init(): void {
    if (!this.disposed) {
      return;
    }
    this.disposed = false;
    this.searchController.init();
    this.activateInnerControllers();
  }

  // 이름 검색이다. 입력 중에는 debounce 뒤에 커밋하고, 제출(Enter·버튼)은 즉시 커밋한다. 타이핑
  // 문법은 없다. 라벨 조건은 콤보박스 후보에서 골라 addLabelToken 으로 추가한다(rules.md §17).
  setSearchInput(value: string): void {
    this.searchController.setInputValue(value);
  }

  submitSearch(): void {
    this.searchController.submit();
  }

  // 라벨 조건 하나를 적용한다. 이미 적용된 조건은 그대로 둔다.
  addLabelToken(token: SearchToken): void {
    if (this.labelTokens.some((current) => current.value === token.value)) {
      return;
    }
    this.setLabelTokens([...this.labelTokens, token]);
  }

  // 검색 콤보박스에서 후보를 고른 경우다. 조건을 적용하고 검색 입력을 비운다. 그 순간의 입력("du")은
  // 후보를 찾던 글자이고 이름 검색이 아니다. 비우지 않으면 debounce 가 그 글자를 이름 검색으로
  // 커밋한다. 그러면 "Duration: long" 옆에 "검색: du" 도 표시된다.
  //
  // 후보 값은 `key:value` 형식 하나이고, 키가 축을 정한다. 카탈로그에 facet 그룹이 있는 키는 facet
  // 선택에 추가한다. 그래서 드롭다운·프리셋·칩이 같은 상태를 본다. 나머지는 라벨 조건으로 적용한다.
  selectLabelCandidate(token: SearchToken): void {
    const pair = splitLabelToken(token.value);
    const filterSnapshot = this.filterController.getSnapshot();
    const group =
      pair === null
        ? undefined
        : filterSnapshot.groups.find((candidate) => candidate.value === pair[0]);
    if (pair !== null && group !== undefined) {
      const [key, value] = pair;
      const current = filterSnapshot.selection[key] ?? [];
      if (!current.includes(value)) {
        this.filterController.setGroupSelection(key, [...current, value]);
      }
    } else {
      this.addLabelToken(token);
    }
    this.searchController.clear();
  }

  // 라벨 조건 하나를 해제한다(적용 조건 칩 ✕).
  removeLabelToken(value: string): void {
    if (!this.labelTokens.some((current) => current.value === value)) {
      return;
    }
    this.setLabelTokens(this.labelTokens.filter((current) => current.value !== value));
  }

  // 커밋 기준의 최종 조건(검색어·병합 selection·일치 방식)이다. 값은 생성자 콜백 2종으로 보내는
  // 값과 같다. 탭 전환처럼 criteria 콜백 대상 소스를 교체하는 호출자가, 새 활성 소스에 같은 조건을
  // 적용할 때 읽는다. 호출자가 조건 상태를 중복 저장하지 않게 하는 API 다.
  currentCriteria(): {
    query: string;
    selection: FilterSelection;
    matchMode: DatasetMatchMode;
  } {
    return {
      query: this.searchController.getSnapshot().query,
      selection: this.mergedSelection(),
      matchMode: this.matchMode,
    };
  }

  // 북마크·기억 저장 스코프(uid·표면)를 설정한다. 스코프가 실제로 바뀔 때만 그 스코프의 저장값을 다시 읽는다.
  // uid 는 로그인이 끝난 뒤 늦게 채워질 수 있다. 그래서 뷰가 렌더마다 호출해도 안전해야 한다(멱등).
  setBookmarkScope(uid: string | null, surface: DatasetFilterSurface): void {
    if (this.bookmarkScopeLoaded && uid === this.bookmarkUid && surface === this.bookmarkSurface) {
      return;
    }
    this.bookmarkUid = uid;
    this.bookmarkSurface = surface;
    this.bookmarkScopeLoaded = true;
    this.bookmarks = loadFilterBookmarks(uid, surface);
    // 필터 조건 기억(opt-in)이다. 켜져 있으면 저장된 값을 복원 대기 값으로 둔다(카탈로그를 로드한 뒤 적용).
    const memory = loadFilterMemory(uid, surface);
    this.rememberFilters = memory.remember;
    this.pendingRestore = memory.remember ? memory.values : null;
    this.tryApplyPendingRestore();
    this.refresh();
  }

  // 필터 조건 기억 스위치다. 켜면 현재 조건을 즉시 저장하고, 끄면 저장된 값도 함께 지운다.
  setRememberFilters(remember: boolean): void {
    if (remember === this.rememberFilters) {
      return;
    }
    this.rememberFilters = remember;
    this.pendingRestore = null;
    if (remember) {
      this.persistFilterValues();
    } else {
      saveFilterMemory(this.bookmarkUid, this.bookmarkSurface, { remember: false, values: null });
    }
    this.refresh();
  }

  // 복원 대기 값을 적용한다. 그룹이 로드되어 있어야 normalize 가 setGroupSelection 의 값을 버리지 않는다.
  // 로드 전이면 loadCatalog 가 성공한 뒤 다시 호출한다. 공개 세터를 거치므로 병합 커밋도 함께 실행된다.
  private tryApplyPendingRestore(): void {
    if (this.pendingRestore === null || this.filterController.getSnapshot().groups.length === 0) {
      return;
    }
    const restore = this.pendingRestore;
    this.pendingRestore = null;
    for (const [key, values] of Object.entries(restore.selection)) {
      this.filterController.setGroupSelection(key, [...values]);
    }
    this.matchMode = restore.matchMode;
    this.setLabelTokens(restore.labelTokens);
  }

  // 조건 값을 저장한다(기억 ON 일 때만). 검색어는 저장하지 않는다.
  private persistFilterValues(): void {
    if (!this.rememberFilters) {
      return;
    }
    saveFilterMemory(this.bookmarkUid, this.bookmarkSurface, {
      remember: true,
      values: {
        selection: this.facetSelection,
        labelTokens: this.labelTokens.map((token) => ({ value: token.value, label: token.label })),
        matchMode: this.matchMode,
      },
    });
  }

  // 북마크를 토글한다. 등록하거나 해제한 뒤 즉시 저장한다(개수 제한 없음).
  toggleBookmark(entry: FilterBookmark): void {
    if (this.bookmarks.includes(entry)) {
      this.bookmarks = removeFilterBookmark(this.bookmarks, entry);
    } else {
      const result = addFilterBookmark(this.bookmarks, entry);
      if (!result.ok) {
        // duplicate 는 위 includes 분기에서 걸러진다. 도달할 수 없는 경우를 막는 방어 코드다.
        return;
      }
      this.bookmarks = result.bookmarks;
    }
    saveFilterBookmarks(this.bookmarkUid, this.bookmarkSurface, this.bookmarks);
    this.refresh();
  }

  openAdvancedSheet(): void {
    if (!this.advancedOpen) {
      this.advancedOpen = true;
      this.refresh();
    }
  }

  closeAdvancedSheet(): void {
    if (this.advancedOpen) {
      this.advancedOpen = false;
      this.refresh();
    }
  }

  // 라벨 토큰 검색 입력값(자동완성 필터)이다. 커밋하지 않고 표시만 갱신한다.
  setLabelTokenInput(value: string): void {
    if (value === this.labelTokenInput) {
      return;
    }
    this.labelTokenInput = value;
    this.refresh();
  }

  // 라벨 토큰 선택이 바뀌면 facet 선택과 병합해 커밋한다.
  setLabelTokens(tokens: readonly SearchToken[]): void {
    this.labelTokens = tokens;
    this.refresh();
    this.commitMergedSelection();
  }

  // 일치 방식(any/all)이 바뀌면 표시를 갱신한 뒤 병합 커밋한다. 선택이 없으면 query 가 같으므로 소스가 건너뛴다.
  setMatchMode(mode: DatasetMatchMode): void {
    if (mode === this.matchMode) {
      return;
    }
    this.matchMode = mode;
    this.refresh();
    this.commitMergedSelection();
  }

  // 이름 검색만 비운다(검색 칩 ✕). 입력과 커밋 값을 함께 비워서 목록이 바로 넓어진다.
  clearSearch(): void {
    this.searchController.clear();
  }

  // 이름 검색, facet 선택, 라벨 조건을 한 번에 비운다(칩 행 끝 초기화). 이미 비어 있으면
  // filterController.clearAll() 이 selection 변경 이벤트를 내지 않을 수 있다. 그래서 병합 커밋을 직접 예약한다.
  clearAll(): void {
    this.labelTokens = [];
    this.matchMode = "all";
    this.searchController.clear();
    this.filterController.clearAll();
    this.commitMergedSelection();
    this.refresh();
  }

  // 검색·facet·라벨·조합 방식을 서비스 앱이 생성 시 선언한 기준으로 함께 되돌린다.
  // 사용자 기억값은 현재 선택의 저장소일 뿐 reset 기준이 아니다.
  reset(): void {
    this.labelTokenInput = "";
    this.labelTokens = copySearchTokens(this.defaultLabelTokens);
    this.matchMode = this.defaultMatchMode;
    this.searchController.reset();
    this.filterController.reset();
    // facet 이 이미 기본값이면 FilterController 가 selection 이벤트를 내지 않는다. 그래서 항상 한 번 예약한다.
    this.commitMergedSelection();
    this.refresh();
  }

  async loadCatalog(): Promise<void> {
    if (this.disposed) {
      return;
    }
    this.loading = true;
    this.errorMessage = null;
    this.refresh();
    try {
      const { groups, labelTokenOptions, labelColors } = await loadDatasetLabelFilterCatalog();
      if (this.disposed) {
        return;
      }
      this.catalogGroups = groups;
      this.filterController.setGroups(
        this.facetCounts === null ? groups : applyDatasetFacetCounts(groups, this.facetCounts),
      );
      this.labelTokenOptions = labelTokenOptions;
      this.labelColors = labelColors;
      this.loading = false;
      this.errorMessage = null;
      // 그룹을 로드했으므로 대기 중인 기억 조건이 있으면 지금 적용한다. 이제 normalize 가 값을 버리지 않는다.
      this.tryApplyPendingRestore();
    } catch (error) {
      if (this.disposed) {
        return;
      }
      this.catalogGroups = [];
      this.filterController.setGroups([]);
      this.labelTokenOptions = [];
      this.loading = false;
      this.errorMessage = this.options.resolveErrorMessage(error);
    }
    this.refresh();
  }

  /**
   * 게이트(폴더, 저장 위치) 안의 선택지별 데이터셋 수를 받아 드롭다운 배지로 표시한다
   * (`GET /v1/datasets/facets`).
   *
   * 모집단이 available + 게이트다. 그래서 그 목록을 보는 화면(Dataset 탭)만 호출한다. 보관됨·북마크·
   * 리뷰 큐는 `clearFacetCounts` 로 배지를 제거한다. 개수는 다른 활성 필터와 독립이다. 그래서 필터가
   * 바뀌어도 다시 받지 않는다. 게이트를 바꾸거나 목록을 바꾸는 작업을 한 뒤 호출자가 다시 호출한다.
   *
   * 받는 동안 `loading` 을 true 로 바꾸지 않는다. 수가 늦게 와도 컨트롤은 바로 쓸 수 있어야 한다.
   * 실패하면 배지만 빠진다. 목록은 이 응답 없이 동작한다.
   */
  async loadFacetCounts(gates: DatasetFacetGates): Promise<void> {
    if (this.disposed) {
      return;
    }
    this.facetRequest += 1;
    const request = this.facetRequest;
    let facets: DatasetFacets | null = null;
    try {
      facets = await getDatasetFacets(gates);
    } catch {
      facets = null;
    }
    if (this.disposed || request !== this.facetRequest) {
      return;
    }
    this.facetCounts = facets;
    this.filterController.setGroups(
      facets === null
        ? stripFacetCounts(this.filterController.getSnapshot().groups)
        : applyDatasetFacetCounts(this.catalogGroups, facets),
    );
    this.refresh();
  }

  // 배지를 제거한다. 모집단이 다른 목록으로 바뀐 경우다. 선택지는 그대로 두고 개수만 뺀다. 그래서
  // 적용한 조건은 유지된다. 진행 중인 요청의 응답도 버린다.
  clearFacetCounts(): void {
    this.facetRequest += 1;
    if (this.facetCounts === null) {
      return;
    }
    this.facetCounts = null;
    this.filterController.setGroups(stripFacetCounts(this.filterController.getSnapshot().groups));
    this.refresh();
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    // 대기 중인 커밋이 dispose 뒤에 실행되지 않도록 먼저 취소한다. 그 다음 내부 컨트롤러를 정리한다.
    this.filterCommitDebounce.cancel();
    for (const unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
    this.unsubscribers.length = 0;
    this.filterController.dispose();
    this.searchController.dispose();
    this.listeners.clear();
  }

  // 내부 컨트롤러(검색·facet) 구독을 한 번만 연결한다. dispose() 가 구독을 끊으면 다음 init() 에서
  // 명시적으로 다시 연결한다.
  private activateInnerControllers(): void {
    if (this.unsubscribers.length > 0) {
      return;
    }
    this.unsubscribers.push(this.searchController.subscribe(this.handleInnerChange));
    this.unsubscribers.push(this.filterController.subscribe(this.handleInnerChange));
    // facet 선택을 저장한 뒤 라벨 토큰과 병합해 커밋한다. 재구독 경로라서 StrictMode 재사용 뒤에도 유지된다.
    this.unsubscribers.push(
      this.filterController.subscribeSelectionChange((selection) => {
        this.facetSelection = selection;
        this.commitMergedSelection();
      }),
    );
  }

  // facet 선택과 라벨 토큰을 하나의 selection 으로 병합하고 debounce 뒤에 알린다. 라벨 토큰 옵션은
  // facet key 를 제외한다. 그래서 두 key 공간이 겹치지 않고, 단순 병합이 안전하다.
  private commitMergedSelection(): void {
    this.filterCommitDebounce.schedule(() => {
      if (!this.disposed) {
        this.options.onFilterSelectionChange(this.mergedSelection(), this.matchMode);
        // 모든 조건 변경이 거치는 유일한 경로다. 기억이 켜져 있으면 커밋 시점의 값을 함께 저장한다.
        this.persistFilterValues();
      }
    });
  }

  private mergedSelection(): FilterSelection {
    const merged: Record<string, readonly string[]> = { ...this.facetSelection };
    const tokensByKey = new Map<string, string[]>();
    for (const token of this.labelTokens) {
      const pair = splitLabelToken(token.value);
      if (!pair) {
        continue;
      }
      const [key, value] = pair;
      const values = tokensByKey.get(key) ?? [];
      values.push(value);
      tokensByKey.set(key, values);
    }
    for (const [key, values] of tokensByKey) {
      merged[key] = values;
    }
    return merged;
  }

  private readonly handleInnerChange = (): void => {
    this.refresh();
  };

  private refresh(): void {
    this.snapshot = this.computeSnapshot();
    for (const listener of this.listeners) {
      listener();
    }
  }

  private computeSnapshot(): DatasetFilterSnapshot {
    return {
      search: this.searchController.getSnapshot(),
      filter: this.filterController.getSnapshot(),
      labelTokens: this.labelTokens,
      labelTokenInput: this.labelTokenInput,
      labelTokenOptions: this.labelTokenOptions,
      labelColors: this.labelColors,
      matchMode: this.matchMode,
      bookmarks: this.bookmarks,
      advancedOpen: this.advancedOpen,
      rememberFilters: this.rememberFilters,
      facetCountsShown: this.facetCounts !== null,
      loading: this.loading,
      errorMessage: this.errorMessage,
    };
  }
}

function copySearchTokens(tokens: readonly SearchToken[]): readonly SearchToken[] {
  return tokens.map((token) => ({ ...token }));
}
